<template>
  <div v-if="ready" class="sp-index-filters">
    <sp-menu
      ref="menu"
      :label="menuLabel"
      location="bottom-right"
      native-content-clicks
      :close-on-content-click="false"
      @update-open="handleOpen"
    >
      <IndexFiltersActivator slot="activator" :count="count" />
      <IndexFiltersPanel @change="handleChange" @choose="openChooser" @retry="reload" />
      <sp-filter-chooser-app
        v-if="store.activeChooser"
        ref="chooser"
        :index="index"
        :save-url="saveUrl"
        @filters-chosen="reload"
      ></sp-filter-chooser-app>
    </sp-menu>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, useTemplateRef } from "vue";
import { whenMountReady } from "#ce/framework/mount-ready";
import { useExpose } from "#ce/composables/useExpose";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { props as appProps } from "./props";
import { useIndexFiltersStore, FilterStatus } from "#ce/apps/SpAdmin/SpIndexFilters/store/index-filters";
import { t, whenI18nReady } from "./i18n";
import IndexFiltersActivator from "#ce/apps/SpAdmin/SpIndexFilters/components/IndexFiltersActivator.ce.vue";
import IndexFiltersPanel from "#ce/apps/SpAdmin/SpIndexFilters/components/IndexFiltersPanel.ce.vue";

defineOptions({ inheritAttrs: false });

const props = defineProps(appProps);
const emit = defineEmits(["index-filters-ready", "index-filters-disconnected"]);
const { emitBubbling } = useHostEmit();
const { exposeMethods, exposeProperties } = useExpose();
const store = useIndexFiltersStore();
const menu = useTemplateRef("menu");
const chooser = useTemplateRef("chooser");
const ready = ref(false);
const count = computed(() => (store.tabs.length ? store.appliedCount : Number(props.appliedCount)));
const menuLabel = computed(() => t("filters"));

async function initialize() {
  store.configure(props);
  await whenI18nReady;
  ready.value = true;

  if (await store.initialize()) {
    emit("index-filters-ready", store.snapshot);
  }
}

async function reload() {
  if (await store.load()) {
    emit("index-filters-ready", store.snapshot);
  }
}

function handleChange() {
  emitBubbling("change", store.snapshot);
}

async function handleOpen({ detail }) {
  if (await store.open(detail[0])) {
    emit("index-filters-ready", store.snapshot);
  }
}

function show() {
  menu.value?.show();
}

async function setValue(param, value) {
  await store.ensureLoaded();

  const changed = store.setValue(param, value);

  if (changed) {
    handleChange();
  }

  return changed;
}

function clear(param, selected) {
  store.clear(param, selected);
  handleChange();
}

function clearAll() {
  store.clearAll();
  handleChange();
}

function getFilterState() {
  return store.tabs.length || store.status === FilterStatus.READY ? store.snapshot : null;
}

async function openChooser() {
  const choice = store.activeChooser;
  if (choice) {
    await whenMountReady(chooser.value);
    chooser.value.open(choice.entries, { list: choice.list });
  }
}

function disconnect() {
  emit("index-filters-disconnected");
}

exposeMethods({ show, clear, clearAll, setValue, getFilterState, reload });
exposeProperties({
  bridgeLabels: { get: () => ({ clearAll: t("clear_all"), showFilter: t("show_filter") }) },
});
onMounted(initialize);
onBeforeUnmount(disconnect);
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped>
.sp-index-filters > sp-menu {
  --sp-comp-menu-width: max-content;
  --sp-comp-menu-content-padding: var(--sp-comp-admin-index-filters-panel-menu-padding-block, 0px) 0;
}
</style>
